import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../lib/cn';

// docs/06 §5: primary = tick-ink fill, pill radius, hover moss; secondary = white with tick border; destructive = outline only.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-pill font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-tick',
  {
    variants: {
      variant: {
        primary: 'bg-tick-ink text-white hover:bg-moss',
        secondary: 'bg-surface text-tick-ink border border-tick hover:bg-mint-2',
        destructive: 'bg-surface text-bad border border-bad hover:bg-bad-bg',
        ghost: 'text-ink-2 hover:bg-mint-2',
      },
      size: { md: 'h-10 px-5 text-sm', lg: 'h-12 px-6 text-[15px]', sm: 'h-8 px-3 text-[13px]' },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> { asChild?: boolean }

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';
  return <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />;
});
Button.displayName = 'Button';
